import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import {
  getWalletSpendSummary,
  getWalletTransactions,
} from "../apis/wallet";
import { fmtINR } from "../utils/money";
import { fmtIST } from "../utils/datetime";
import {
  CATEGORY_LABELS,
  defaultSpendRange,
  isCredit,
  parseMeta,
  txnSub,
  txnTitle,
} from "../utils/walletTxn";

const PAGE_SIZE = 20;

const WalletSpend = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const range = defaultSpendRange(30);
  const from = params.get("from") || range.from;
  const to = params.get("to") || range.to;
  const type = params.get("type") || "";
  const category = params.get("category") || "";
  const campaignId = params.get("campaign_id") || "";
  const q = params.get("q") || "";
  const view = params.get("view") === "txns" ? "txns" : "campaigns";
  const page = Math.max(0, parseInt(params.get("page") || "0", 10) || 0);

  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [txnLoading, setTxnLoading] = useState(false);

  const filterQs = useMemo(
    () => ({
      from,
      to,
      ...(type ? { type } : {}),
      ...(category ? { category } : {}),
      ...(campaignId ? { campaign_id: campaignId } : {}),
      ...(q ? { q } : {}),
    }),
    [from, to, type, category, campaignId, q]
  );

  const setFilter = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v == null) next.delete(k);
      else next.set(k, String(v));
    });
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWalletSpendSummary({ from, to });
      if (res.data?.success) setSummary(res.data.data);
    } catch (_) {
      toast.error("Failed to load spend");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  const loadTxns = useCallback(async () => {
    if (view !== "txns") return;
    setTxnLoading(true);
    try {
      const res = await getWalletTransactions({
        ...filterQs,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      if (res.data?.success) setTxns(res.data.data);
    } catch (_) {
      toast.error("Failed to load transactions");
    } finally {
      setTxnLoading(false);
    }
  }, [view, filterQs, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadTxns(); }, [loadTxns]);

  const totals = summary?.totals;
  const campaigns = summary?.campaigns || [];
  const filteredCampaigns = campaignId
    ? campaigns.filter((c) => String(c.campaign_id || "") === campaignId)
    : campaigns;
  const totalPages = Math.max(1, Math.ceil((txns.total || 0) / PAGE_SIZE));

  return (
    <div className="wallet-page wspend">
      <div className="page-head">
        <Link to="/wallet" className="wspend-back">
          <ArrowLeft size={15} />
          Wallet
        </Link>
        <div>
          <h2>Spend details</h2>
          <p>How much each campaign cost, with refunds netted out</p>
        </div>
        <div className="grow" />
        <button
          type="button"
          className="ping-btn ping-btn-ghost ping-btn-sm"
          onClick={() => { loadSummary(); loadTxns(); }}
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      <div className="wspend-filters">
        <label>
          From
          <input type="date" value={from} onChange={(e) => setFilter({ from: e.target.value })} />
        </label>
        <label>
          To
          <input type="date" value={to} onChange={(e) => setFilter({ to: e.target.value })} />
        </label>
        <label>
          Type
          <select value={type} onChange={(e) => setFilter({ type: e.target.value })}>
            <option value="">All types</option>
            <option value="debit">Message charges</option>
            <option value="refund">Refunds</option>
            <option value="recharge">Recharges</option>
            <option value="adjustment">Adjustments</option>
          </select>
        </label>
        <label>
          Category
          <select value={category} onChange={(e) => setFilter({ category: e.target.value })}>
            <option value="">All categories</option>
            {Object.entries(CATEGORY_LABELS).map(([k, lab]) => (
              <option key={k} value={k}>{lab}</option>
            ))}
          </select>
        </label>
        <label>
          Campaign
          <select value={campaignId} onChange={(e) => setFilter({ campaign_id: e.target.value })}>
            <option value="">All campaigns</option>
            {campaigns.filter((c) => c.campaign_id).map((c) => (
              <option key={c.campaign_id} value={c.campaign_id}>{c.campaign_name}</option>
            ))}
          </select>
        </label>
        <label className="wspend-q">
          Phone
          <input
            type="search"
            placeholder="Search number"
            value={q}
            onChange={(e) => setFilter({ q: e.target.value })}
          />
        </label>
      </div>

      <div className="wspend-totals">
        <div>
          <span className="k">Gross charged</span>
          <b>{fmtINR(totals?.gross_debit || 0)}</b>
          <small>{(totals?.debit_n || 0).toLocaleString("en-IN")} messages</small>
        </div>
        <div>
          <span className="k">Refunds</span>
          <b className="cr">{fmtINR(totals?.refunds || 0)}</b>
          <small>{(totals?.refund_n || 0).toLocaleString("en-IN")} failed</small>
        </div>
        <div>
          <span className="k">Net spend</span>
          <b>{fmtINR(totals?.net_spend || 0)}</b>
          <small>After refunds</small>
        </div>
        <div>
          <span className="k">Money added</span>
          <b className="cr">{fmtINR(totals?.recharge || 0)}</b>
          <small>Recharges in range</small>
        </div>
      </div>

      <div className="ping-pill-tabs">
        <button
          type="button"
          className={`ping-ptab ${view === "campaigns" ? "on" : ""}`}
          onClick={() => setFilter({ view: "campaigns" })}
        >
          By campaign
        </button>
        <button
          type="button"
          className={`ping-ptab ${view === "txns" ? "on" : ""}`}
          onClick={() => setFilter({ view: "txns", page: 0 })}
        >
          All transactions
        </button>
      </div>

      {view === "campaigns" ? (
        <div className="wallet-card">
          {loading ? (
            <div className="wallet-empty">
              <Loader2 className="animate-spin mx-auto" size={22} style={{ color: "var(--blue)" }} />
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="wallet-empty">No campaign spend in this range</div>
          ) : (
            <table className="wallet-table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Category</th>
                  <th style={{ textAlign: "right" }}>Billed</th>
                  <th style={{ textAlign: "right" }}>Refunded</th>
                  <th style={{ textAlign: "right" }}>Gross</th>
                  <th style={{ textAlign: "right" }}>Net</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.map((c) => (
                  <tr
                    key={c.campaign_id || "other"}
                    className={c.campaign_id ? "wspend-row" : undefined}
                    onClick={() => {
                      if (c.campaign_id) navigate(`/wallet/spend/${c.campaign_id}?from=${from}&to=${to}`);
                    }}
                  >
                    <td>
                      <div className="wallet-cell">
                        <div>
                          <b>{c.campaign_name}</b>
                          <span className="sub">{fmtIST(c.last_at)}</span>
                        </div>
                      </div>
                    </td>
                    <td className="wspend-cats">
                      {(c.categories || []).map((cat) => CATEGORY_LABELS[cat] || cat).join(", ") || "—"}
                    </td>
                    <td style={{ textAlign: "right" }}>{c.billed_n.toLocaleString("en-IN")}</td>
                    <td style={{ textAlign: "right" }}>{c.refund_n.toLocaleString("en-IN")}</td>
                    <td style={{ textAlign: "right" }}>{fmtINR(c.gross)}</td>
                    <td style={{ textAlign: "right" }}>
                      <b>{fmtINR(c.net)}</b>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="wallet-card wallet-activity">
          <div className="wallet-activity-body">
            {txnLoading ? (
              <div className="wallet-empty">
                <Loader2 className="animate-spin mx-auto" size={22} style={{ color: "var(--blue)" }} />
              </div>
            ) : txns.rows.length === 0 ? (
              <div className="wallet-empty">No transactions match these filters</div>
            ) : (
              <TxnTable rows={txns.rows} />
            )}
          </div>
          {txns.total > 0 && (
            <div className="wallet-pager">
              <span>Page {page + 1} of {totalPages} · {txns.total.toLocaleString("en-IN")} transactions</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setFilter({ page: page - 1 })}
                  className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setFilter({ page: page + 1 })}
                  className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function TxnTable({ rows }) {
  return (
    <table className="wallet-table">
      <thead>
        <tr>
          <th>Transaction</th>
          <th>Date</th>
          <th style={{ textAlign: "right" }}>Amount</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((t) => {
          const meta = parseMeta(t.meta);
          const credit = isCredit(t.type, meta, t.sign);
          return (
            <tr key={t.id}>
              <td>
                <div className="wallet-cell">
                  <span className={`wallet-dir ${credit ? "cr" : "db"}`} aria-hidden><i /></span>
                  <div>
                    <b>{txnTitle(t.type, meta, t.resolved)}</b>
                    <span className="sub">{txnSub(t.type, meta, t.reference_id, t.resolved)}</span>
                  </div>
                </div>
              </td>
              <td style={{ color: "var(--text-2)", whiteSpace: "nowrap" }}>{fmtIST(t.created_at)}</td>
              <td style={{ textAlign: "right" }}>
                <span className={credit ? "wallet-amt-cr" : "wallet-amt-db"}>
                  {credit ? "+" : "−"} {fmtINR(t.amount)}
                </span>
              </td>
              <td style={{ textAlign: "right", width: 1, whiteSpace: "nowrap" }}>
                <Link to={`/wallet/transactions/${t.id}`} className="ping-btn ping-btn-ghost ping-btn-sm">
                  Details
                </Link>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default WalletSpend;
